import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../LoopinoDesktopGuidelines.css';
import './DesktopCalendarPage.css';
import useApi from '../../../hooks/useApi';
import useCampaign from '../../../hooks/useCampaign';
import useLanguage from '../../../hooks/useLanguage';
import postService from '../../../services/PostService';
import { getFirstMediaUrl, isVideoFile } from '../../../utils/mediaHelpers';
import { Skeleton } from '../../effects';
import DesktopCampaignSelector from '../DesktopCampaignSelector/DesktopCampaignSelector';
import DesktopHorizontalScroll from '../common/scroll/DesktopHorizontalScroll';

const DEFAULT_EVENT_DURATION_MINUTES = 180;
const MIN_EVENT_DURATION_MINUTES = 210;
const HOUR_CELL_MIN_HEIGHT = 72;
const STACK_GAP_PX = 10;
const START_HOUR = 0;
const END_HOUR = 24;

const PlatformIcon = ({ platform, size = 14 }) => {
  const icons = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="currentColor" strokeWidth="2" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="currentColor" strokeWidth="2" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 8C18.5259 8 20.5259 10 20.5259 12.5V19H16.5259V12.5C16.5259 11.5 15.5259 11 14.5259 11C13.5259 11 12.5259 11.5 12.5259 12.5V19H8.52588V8H12.5259V9.5C13.5259 8.5 14.5259 8 16 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="2" y="9" width="4" height="12" stroke="currentColor" strokeWidth="2" />
        <circle cx="4" cy="4" r="2" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    x: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4L20 20M20 4L4 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    )
  };

  return icons[platform] || icons.instagram;
};

const StatusDot = ({ variant = 'pending' }) => (
  <span className={`loopino-calendar-status-dot ${variant}`} aria-hidden="true" />
);

const normalizePostIdValue = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    if (value.$oid) {
      return String(value.$oid);
    }

    if (value.id || value._id) {
      return normalizePostIdValue(value.id ?? value._id);
    }

    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return String(stringified);
      }
    }
  }

  return null;
};

const getPostIdentifier = (post) => {
  if (!post) return null;

  return (
    normalizePostIdValue(post.id) ||
    normalizePostIdValue(post._id) ||
    normalizePostIdValue(post.post_id) ||
    normalizePostIdValue(post.postId) ||
    null
  );
};

const getPostProfileName = (post) => {
  if (!post) return '';

  const candidates = [
    post.profileName,
    post.profile_name,
    post.accountName,
    post.account_name,
    post.pageName,
    post.page_name,
    post.socialProfileName,
    post.social_profile_name,
    post.profile?.name,
    post.social_profile?.name,
    post.account?.name,
    post.page?.name,
    post.user?.name,
    post.username,
    post.owner?.name
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
    if (candidate && typeof candidate === 'object') {
      const value = candidate.displayName || candidate.username || candidate.name;
      if (value && typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }

  return '';
};

const getPostScheduledDate = (post) => {
  if (!post) return null;
  const rawValue =
    post.scheduledDate ||
    post.scheduled_time ||
    post.scheduledTime ||
    post.scheduled_at ||
    post.scheduledAt;

  if (!rawValue) return null;

  const normalized = typeof rawValue === 'object' && rawValue.$date
    ? rawValue.$date
    : rawValue;

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildPostReviewHref = (post) => {
  const params = new URLSearchParams();
  const postId = getPostIdentifier(post);
  if (postId) {
    params.set('postId', postId);
  }

  const scheduledAt = getPostScheduledDate(post);
  if (scheduledAt) {
    try {
      params.set('scheduledAt', scheduledAt.toISOString());
    } catch (error) {
      // ignore invalid date serialization
    }
  }

  const query = params.toString();
  return `/desktop/posts${query ? `?${query}` : ''}`;
};

const getTargetPostIdFromSearch = (search = '') => {
  const source = search || (typeof window !== 'undefined' ? window.location.search : '');
  if (!source) return null;
  try {
    const params = new URLSearchParams(source);
    return params.get('postId') || params.get('targetPostId') || null;
  } catch (error) {
    return null;
  }
};

const getTargetDateFromSearch = (search = '') => {
  const source = search || (typeof window !== 'undefined' ? window.location.search : '');
  if (!source) return null;
  try {
    const params = new URLSearchParams(source);
    const raw = params.get('scheduledAt') || params.get('targetDate');
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch (error) {
    return null;
  }
};

const getCalendarPostStatusInfo = (post) => {
  const rawStatus = post?.status;
  let normalizedStatus = '';

  if (typeof rawStatus === 'string') {
    normalizedStatus = rawStatus.toLowerCase();
  } else if (rawStatus && typeof rawStatus === 'object') {
    const candidate = rawStatus.value || rawStatus.current || rawStatus.status || rawStatus.state || rawStatus.name;
    normalizedStatus = candidate ? candidate.toString().toLowerCase() : '';
  } else if (rawStatus !== undefined && rawStatus !== null) {
    normalizedStatus = String(rawStatus).toLowerCase();
  }

  const isExplicitlyPosted = post?.posted === true;

  if (isExplicitlyPosted || normalizedStatus === 'published' || normalizedStatus === 'posted') {
    return {
      badgeVariant: 'posted',
      cardClass: 'posted',
      isApproved: true
    };
  }

  if (normalizedStatus === 'failed') {
    return {
      badgeVariant: 'failed',
      cardClass: 'failed',
      isApproved: false
    };
  }

  if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled') {
    return {
      badgeVariant: 'cancelled',
      cardClass: 'cancelled',
      isApproved: false
    };
  }

  if (normalizedStatus === 'publishing' || normalizedStatus === 'processing') {
    return {
      badgeVariant: 'publishing',
      cardClass: 'publishing',
      isApproved: true
    };
  }

  if (normalizedStatus === 'queued' || normalizedStatus === 'scheduled') {
    return {
      badgeVariant: 'scheduled',
      cardClass: 'scheduled',
      isApproved: true
    };
  }

  return {
    badgeVariant: 'pending',
    cardClass: 'draft',
    isApproved: false
  };
};

const getStatusBorderColor = (variant) => {
  const map = {
    posted: 'var(--loopino-success)',
    scheduled: 'var(--loopino-brand-primary)',
    publishing: 'var(--loopino-brand-primary)',
    failed: 'var(--loopino-error)',
    cancelled: 'var(--loopino-error)',
    pending: 'var(--loopino-warning)',
    draft: 'var(--loopino-warning)'
  };

  return map[variant] || 'var(--loopino-border-soft)';
};

const CampaignBadge = ({ label, active }) => (
  <span className={`loopino-calendar-campaign-chip ${active ? 'is-active' : ''}`}>{label}</span>
);

const DesktopCalendarPage = ({
  posts = [],
  campaigns = [],
  onPostClick = null,
  onNavigate = null,
  targetDate = null,
  queryString = ''
}) => {
  const effectiveSearch = useMemo(() => {
    if (queryString) return queryString;
    if (typeof window !== 'undefined') return window.location.search || '';
    return '';
  }, [queryString]);

  const targetPostIdFromQuery = useMemo(() => getTargetPostIdFromSearch(effectiveSearch), [effectiveSearch]);
  const targetDateFromQuery = useMemo(() => getTargetDateFromSearch(effectiveSearch), [effectiveSearch]);

  const api = useApi();
  const { campaigns: apiCampaigns, loadCampaigns } = useCampaign();
  const { t, language } = useLanguage();

  const [currentWeek, setCurrentWeek] = useState(() => targetDate || targetDateFromQuery || new Date());
  const [calendarPosts, setCalendarPosts] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState(null);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // week | agenda
  const [currentTime, setCurrentTime] = useState(new Date());
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [thumbnailsLoading, setThumbnailsLoading] = useState({});
  const [serviceReady, setServiceReady] = useState(false);
  const [targetPostId, setTargetPostId] = useState(() => targetPostIdFromQuery);
  const [highlightedPostId, setHighlightedPostId] = useState(null);

  const serviceInitializedRef = useRef(false);
  const calendarCacheRef = useRef(new Map());
  const scrollRef = useRef(null);
  const targetEventRef = useRef(null);
  const hasCenteredTargetRef = useRef(false);
  const hourRowRefs = useRef(new Map());

  const resolvedLocale = useMemo(() => {
    if (!language) return 'it-IT';
    if (language.includes('-')) return language;
    if (language === 'it') return 'it-IT';
    if (language === 'en') return 'en-US';
    return language;
  }, [language]);

  const weekdayFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { weekday: 'short' });
    } catch (error) {
      console.warn('[DesktopCalendar] Falling back to default weekday formatter', error);
      return new Intl.DateTimeFormat('it-IT', { weekday: 'short' });
    }
  }, [resolvedLocale]);

  const monthYearFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { month: 'long', year: 'numeric' });
    } catch (error) {
      console.warn('[DesktopCalendar] Falling back to default month-year formatter', error);
      return new Intl.DateTimeFormat('it-IT', { month: 'long', year: 'numeric' });
    }
  }, [resolvedLocale]);

  const dayMonthFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { day: 'numeric', month: 'short' });
    } catch (error) {
      console.warn('[DesktopCalendar] Falling back to default day-month formatter', error);
      return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' });
    }
  }, [resolvedLocale]);

  const dayMonthYearFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (error) {
      console.warn('[DesktopCalendar] Falling back to default day-month-year formatter', error);
      return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }, [resolvedLocale]);

  const monthLabelFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { month: 'short' });
    } catch (error) {
      console.warn('[DesktopCalendar] Falling back to default month formatter', error);
      return new Intl.DateTimeFormat('it-IT', { month: 'short' });
    }
  }, [resolvedLocale]);

  const allCampaigns = useMemo(() => {
    const combined = [...campaigns, ...apiCampaigns];
    const byId = new Map();
    combined.forEach((campaign) => {
      if (!campaign) return;
      const id = normalizePostIdValue(campaign.id) || normalizePostIdValue(campaign._id);
      if (!id) return;
      if (!byId.has(id)) {
        byId.set(id, { ...campaign, id });
      }
    });
    return Array.from(byId.values());
  }, [apiCampaigns, campaigns]);

  const getWeekDays = useCallback((date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      week.push(d);
    }
    return week;
  }, []);

  const getWeekRange = useCallback((date) => {
    const weekDays = getWeekDays(date);
    const startDate = weekDays[0];
    const endDate = weekDays[6];
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start_date: fmt(startDate), end_date: fmt(endDate) };
  }, [getWeekDays]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < (startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1); i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const isToday = useCallback((date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  const initializeServices = useCallback(() => {
    if (!api || serviceInitializedRef.current) return;
    try {
      postService.initialize(api);
      serviceInitializedRef.current = true;
      setServiceReady(true);
      loadCampaigns({ limit: 200 }).catch((err) => {
        console.error('[DesktopCalendar] Failed to load campaigns', err);
      });
    } catch (error) {
      console.error('[DesktopCalendar] Failed to initialize services', error);
    }
  }, [api, loadCampaigns]);

  const loadCalendarPosts = useCallback(async () => {
    if (!serviceInitializedRef.current) return;
    setCalendarLoading(true);
    setCalendarError(null);

    try {
      const selectionKey = selectedCampaignIds.length === 0
        ? 'all'
        : selectedCampaignIds.slice().sort().join(',');

      if (calendarCacheRef.current.has(selectionKey)) {
        const cached = calendarCacheRef.current.get(selectionKey);
        if (Array.isArray(cached)) {
          setCalendarPosts(cached);
          setCalendarLoading(false);
          return;
        }
      }

      const fetchAllForParams = async (params) => {
        const limit = 100;
        let offset = 0;
        let all = [];
        let iter = 0;
        const maxIterations = 50;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const resp = await postService.listPosts({ ...params, limit, offset });
          const items = resp.posts || [];
          all = all.concat(items);
          const hasNext = resp.pagination?.has_next || items.length === limit;
          offset += items.length;
          iter += 1;
          if (!hasNext || iter >= maxIterations) break;
        }
        return all;
      };

      const hasSelection = selectedCampaignIds.length > 0;
      let merged = [];

      if (!hasSelection) {
        merged = await fetchAllForParams({});
      } else if (selectedCampaignIds.length === 1) {
        merged = await fetchAllForParams({ campaign_id: selectedCampaignIds[0] });
      } else {
        const MULTI_FETCH_THRESHOLD = 10;
        if (selectedCampaignIds.length <= MULTI_FETCH_THRESHOLD) {
          const promises = selectedCampaignIds.map((cid) => fetchAllForParams({ campaign_id: cid }));
          const results = await Promise.all(promises);
          const byId = new Map();
          results.flat().forEach((p) => {
            const pid = p.id || p._id;
            if (!byId.has(pid)) byId.set(pid, p);
          });
          merged = Array.from(byId.values());
        } else {
          merged = await fetchAllForParams({});
        }
      }

      merged.sort((a, b) => {
        const da = new Date(a.scheduledDate || a.scheduled_time || 0).getTime();
        const db = new Date(b.scheduledDate || b.scheduled_time || 0).getTime();
        return da - db;
      });

      setCalendarPosts(merged);
      calendarCacheRef.current.set(selectionKey, merged);
    } catch (error) {
      console.error('[DesktopCalendar] Failed to load posts:', error);
      setCalendarError(error?.message || t('calendar.errors.loadPosts', { defaultValue: 'Errore nel caricamento dei post' }));
    } finally {
      setCalendarLoading(false);
    }
  }, [selectedCampaignIds, t]);

  const displayPosts = useMemo(() => {
    const sourcePosts = calendarPosts.length > 0 ? calendarPosts : posts;
    let filtered = sourcePosts;

    if (selectedCampaignIds.length > 0) {
      const selectedIdSet = new Set(selectedCampaignIds.map((id) => String(id)));
      filtered = filtered.filter((post) => {
        let postCampaignId = post.campaign_id || post.campaignId;
        if (postCampaignId && typeof postCampaignId === 'object') {
          if (postCampaignId.$oid) {
            postCampaignId = postCampaignId.$oid;
          } else if (postCampaignId.toString) {
            postCampaignId = postCampaignId.toString();
          }
        }
        return selectedIdSet.has(String(postCampaignId || ''));
      });
    }

    const { start_date, end_date } = getWeekRange(currentWeek);
    const weekStart = new Date(start_date);
    const weekEnd = new Date(end_date);
    weekEnd.setHours(23, 59, 59, 999);

    filtered = filtered.filter((post) => {
      const postDate = new Date(post.scheduledDate || post.scheduled_time);
      if (!postDate || Number.isNaN(postDate.getTime())) return false;
      return postDate >= weekStart && postDate <= weekEnd;
    });

    return filtered;
  }, [calendarPosts, currentWeek, getWeekRange, posts, selectedCampaignIds]);

  const weekDays = useMemo(() => getWeekDays(currentWeek), [currentWeek, getWeekDays]);
  const hours = useMemo(() => Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i), []);

  const isDateInCurrentWeek = useCallback((date) => {
    if (!date || !weekDays || weekDays.length < 2) return false;
    const start = new Date(weekDays[0]);
    const end = new Date(weekDays[6]);
    end.setHours(23, 59, 59, 999);
    return date >= start && date <= end;
  }, [weekDays]);

  const formattedWeekRange = useMemo(() => {
    if (!weekDays || weekDays.length < 2) return '';
    const start = weekDays[0];
    const end = weekDays[6];

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return t('calendar.week.rangeSameMonth', {
        startDay: start.getDate(),
        endDay: end.getDate(),
        month: monthYearFormatter.format(start),
        defaultValue: `${start.getDate()} - ${end.getDate()} ${monthYearFormatter.format(start)}`
      });
    }

    return t('calendar.week.rangeDifferentMonth', {
      start: dayMonthFormatter.format(start),
      end: dayMonthYearFormatter.format(end),
      defaultValue: `${dayMonthFormatter.format(start)} - ${dayMonthYearFormatter.format(end)}`
    });
  }, [dayMonthFormatter, dayMonthYearFormatter, monthYearFormatter, t, weekDays]);

  const handleWeekChange = (direction) => {
    const nextWeek = new Date(currentWeek);
    nextWeek.setDate(currentWeek.getDate() + direction * 7);
    setCurrentWeek(nextWeek);
  };

  const navigateToToday = () => {
    setCurrentWeek(new Date());
  };

  useEffect(() => {
    if (!targetPostId) return;
    if (highlightedPostId === targetPostId) return;
    const pool = calendarPosts.length > 0 ? calendarPosts : posts;
    const targetPost = pool.find((p) => getPostIdentifier(p) === targetPostId);
    if (!targetPost) return;

    const scheduledAt = getPostScheduledDate(targetPost);
    if (scheduledAt && !isDateInCurrentWeek(scheduledAt)) {
      setCurrentWeek(scheduledAt);
      return;
    }

    setHighlightedPostId(targetPostId);
  }, [calendarPosts, highlightedPostId, isDateInCurrentWeek, posts, targetPostId]);

  useEffect(() => {
    if (!highlightedPostId) return;
    const targetNode = targetEventRef.current;
    if (!targetNode || hasCenteredTargetRef.current) return;
    hasCenteredTargetRef.current = true;

    if (viewMode === 'week' && scrollRef.current) {
      const container = scrollRef.current;
      const containerRect = container.getBoundingClientRect();
      const nodeRect = targetNode.getBoundingClientRect();
      const offset = nodeRect.top - containerRect.top - (container.clientHeight / 2) + (targetNode.clientHeight / 2);
      container.scrollTo({
        top: container.scrollTop + offset,
        behavior: 'smooth'
      });
    } else {
      targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedPostId, viewMode]);

  useEffect(() => {
    hasCenteredTargetRef.current = false;
  }, [highlightedPostId, viewMode]);

  const handlePostCardClick = useCallback((post) => {
    const postId = getPostIdentifier(post);
    const index = displayPosts.findIndex((p) => getPostIdentifier(p) === postId);
    const href = buildPostReviewHref(post);

    if (typeof onPostClick === 'function') {
      onPostClick(post, index, postId);
    }

    if (href) {
      if (typeof onNavigate === 'function') {
        onNavigate(href, { post, index, postId });
      } else if (typeof window !== 'undefined') {
        window.history.pushState({}, '', href);
        window.dispatchEvent(new Event('popstate'));
      }
    }
  }, [displayPosts, onNavigate, onPostClick]);

  const openDatePicker = () => {
    setPickerMonth(currentWeek);
    setIsDatePickerOpen(true);
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    setCurrentWeek(date);
    setIsDatePickerOpen(false);
  };

  const datePickerWeekdays = useMemo(() => {
    const localized = t('calendar.datePicker.weekdays', { returnObjects: true });
    if (Array.isArray(localized) && localized.length === 7) {
      return localized;
    }
    return resolvedLocale.startsWith('en')
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  }, [resolvedLocale, t]);

  const handleCampaignSelectionChange = useCallback((newSelection = []) => {
    const normalized = Array.isArray(newSelection)
      ? newSelection.map((id) => normalizePostIdValue(id)).filter(Boolean)
      : [];
    setSelectedCampaignIds(normalized);
  }, []);

  const selectedCampaignLabels = useMemo(() => {
    if (selectedCampaignIds.length === 0) {
      return [t('calendar.filters.allCampaigns', { defaultValue: 'Tutte le campagne' })];
    }

    const labelMap = new Map();
    allCampaigns.forEach((campaign) => {
      const id = normalizePostIdValue(campaign.id);
      if (id && selectedCampaignIds.includes(id)) {
        labelMap.set(id, campaign.name || campaign.title || `Campagna ${id}`);
      }
    });
    return Array.from(labelMap.values());
  }, [allCampaigns, selectedCampaignIds, t]);

  const postsByDay = useMemo(() => {
    const grouped = new Map();
    displayPosts.forEach((post) => {
      const date = new Date(post.scheduledDate || post.scheduled_time);
      if (!date || Number.isNaN(date.getTime())) return;
      const key = date.toDateString();
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(post);
    });

    weekDays.forEach((day) => {
      const key = day.toDateString();
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
    });

    grouped.forEach((items, key) => {
      items.sort((a, b) => {
        const da = new Date(a.scheduledDate || a.scheduled_time || 0).getTime();
        const db = new Date(b.scheduledDate || b.scheduled_time || 0).getTime();
        return da - db;
      });
    });

    return grouped;
  }, [displayPosts, weekDays]);

  const eventsByDayAndHour = useMemo(() => {
    const structure = new Map();

    weekDays.forEach((day) => {
      const dayKey = day.toDateString();
      const hoursMap = new Map();

      hours.forEach((hour) => {
        hoursMap.set(hour, []);
      });

      const events = postsByDay.get(dayKey) || [];
      events.forEach((post) => {
        const scheduledAt = getPostScheduledDate(post);
        if (!scheduledAt) return;
        const hour = scheduledAt.getHours();
        const minutes = scheduledAt.getMinutes();
        if (!hoursMap.has(hour)) return;

        const rawDuration = Number(post.duration) || DEFAULT_EVENT_DURATION_MINUTES;
        const durationMinutes = Math.max(rawDuration, MIN_EVENT_DURATION_MINUTES);

        hoursMap.get(hour).push({
          post,
          scheduledAt,
          minutes,
          durationMinutes
        });
      });

      hoursMap.forEach((entries) => {
        entries.sort((a, b) => {
          if (a.minutes === b.minutes) {
            const idA = getPostIdentifier(a.post) || '';
            const idB = getPostIdentifier(b.post) || '';
            return idA.localeCompare(idB);
          }
          return a.minutes - b.minutes;
        });
      });

      structure.set(dayKey, hoursMap);
    });

    return structure;
  }, [hours, postsByDay, weekDays]);

  const formatHourLabel = (hour) => `${hour.toString().padStart(2, '0')}:00`;
  const formatTimeLabel = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const generateVideoThumbnail = useCallback((videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;

      const handleLoadedData = () => {
        try {
          const aspectRatio = video.videoWidth / video.videoHeight || 1;
          canvas.width = 120;
          canvas.height = 120 / aspectRatio;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.82);
          resolve(thumbnailDataUrl);
        } catch (error) {
          reject(error);
        } finally {
          video.removeEventListener('loadeddata', handleLoadedData);
          video.removeEventListener('error', handleError);
          video.src = '';
          video.load();
        }
      };

      const handleError = (error) => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        reject(error);
      };

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
      video.src = videoUrl;
      video.currentTime = 0.1;
    });
  }, []);

  useEffect(() => {
    initializeServices();
  }, [initializeServices]);

  useEffect(() => {
    if (targetDate) {
      setCurrentWeek(targetDate);
    }
  }, [targetDate]);

  useEffect(() => {
    if (targetDateFromQuery) {
      setCurrentWeek(targetDateFromQuery);
    }
  }, [targetDateFromQuery]);

  useEffect(() => {
    setTargetPostId(targetPostIdFromQuery);
    setHighlightedPostId(null);
    hasCenteredTargetRef.current = false;
  }, [targetPostIdFromQuery]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!serviceReady) return;
    loadCalendarPosts();
  }, [loadCalendarPosts, serviceReady]);

  useEffect(() => {
    const handlePostUpdated = (e) => {
      const { postId } = e.detail || {};
      if (!postId) return;
      calendarCacheRef.current.clear();
      loadCalendarPosts();
    };

    window.addEventListener('postUpdated', handlePostUpdated);
    window.addEventListener('postDirectUpdate', handlePostUpdated);
    return () => {
      window.removeEventListener('postUpdated', handlePostUpdated);
      window.removeEventListener('postDirectUpdate', handlePostUpdated);
    };
  }, [loadCalendarPosts]);

  useEffect(() => {
    if (viewMode !== 'week') return undefined;

    const container = scrollRef.current;
    if (!container) return undefined;

    let scrollTimeout = null;

    const handleScroll = () => {
      container.classList.add('is-scrolling');
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        container.classList.remove('is-scrolling');
      }, 700);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'week') return;
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const targetHour = 8;
    const targetRow = hourRowRefs.current.get(targetHour);
    const fallbackTop = targetHour * HOUR_CELL_MIN_HEIGHT;
    const offsetTop = targetRow?.offsetTop ?? fallbackTop;
    container.scrollTo({
      top: Math.max(0, offsetTop - 120),
      behavior: 'smooth'
    });
  }, [currentWeek, viewMode]);

  useEffect(() => {
    const buildThumbnails = async () => {
      if (!displayPosts || displayPosts.length === 0) return;
      for (const post of displayPosts) {
        const originalThumbnail = getFirstMediaUrl(post);
        if (!originalThumbnail) continue;

        const thumbnailKey = `${post.id}-${originalThumbnail}`;
        if (isVideoFile(originalThumbnail) && !videoThumbnails[thumbnailKey] && !thumbnailsLoading[thumbnailKey]) {
          setThumbnailsLoading((prev) => ({ ...prev, [thumbnailKey]: true }));
          try {
            const thumb = await generateVideoThumbnail(originalThumbnail);
            setVideoThumbnails((prev) => ({ ...prev, [thumbnailKey]: thumb }));
          } catch (error) {
            console.error('[DesktopCalendar] Error generating video thumbnail', error);
          } finally {
            setThumbnailsLoading((prev) => ({ ...prev, [thumbnailKey]: false }));
          }
        }
      }
    };
    buildThumbnails();
  }, [displayPosts, generateVideoThumbnail, thumbnailsLoading, videoThumbnails]);

  const renderLoadingSkeleton = () => {
    const skeletonDays = Array.from({ length: 7 });
    const skeletonHours = Array.from({ length: 10 });
    const skeletonCampaigns = Array.from({ length: 3 });
    const calendarGridStyle = { '--loopino-calendar-hour-height': '60px' };

    return (
      <>
        <header className="loopino-desktop-post-review-header loopino-desktop-calendar-header">
          <div>
            <p className="loopino-desktop-post-review-eyebrow">
              <Skeleton width={140} height={12} shimmer />
            </p>
            <h1>
              <Skeleton width={320} height={32} shimmer />
            </h1>
            <p className="loopino-desktop-post-review-summary">
              <Skeleton width={520} height={16} shimmer />
            </p>
          </div>
          <div className="loopino-calendar-header-actions">
            <div className="loopino-calendar-chip-group">
              <Skeleton variant="rounded" width={160} height={32} shimmer />
            </div>
            <div className="loopino-calendar-view-toggle">
              <Skeleton variant="rounded" width={220} height={34} shimmer />
            </div>
          </div>
        </header>

        <div className="loopino-desktop-post-strip loopino-desktop-campaign-selector">
          <div className="loopino-desktop-post-strip-header">
            <div>
              <p className="loopino-desktop-post-strip-eyebrow">
                <Skeleton width={120} height={12} shimmer />
              </p>
              <h3 className="loopino-desktop-post-strip-title">
                <Skeleton width={240} height={22} shimmer />
              </h3>
              <p className="loopino-desktop-post-strip-hint">
                <Skeleton width={480} height={14} shimmer />
              </p>
            </div>
            <div className="loopino-desktop-campaign-toolbar">
              <div className="loopino-desktop-campaign-filters">
                <div className="loopino-desktop-campaign-filter">
                  <div className="loopino-desktop-campaign-filter-chips">
                    <Skeleton variant="rounded" width={260} height={32} shimmer />
                  </div>
                </div>
                <div className="loopino-desktop-campaign-filter">
                  <div className="loopino-desktop-campaign-filter-chips">
                    <Skeleton variant="rounded" width={240} height={32} shimmer />
                  </div>
                </div>
              </div>
              <div className="loopino-desktop-campaign-actions">
                <Skeleton variant="rounded" width={120} height={36} shimmer />
                <Skeleton variant="rounded" width={150} height={38} shimmer />
              </div>
            </div>
          </div>
          <DesktopHorizontalScroll
            trackClassName="loopino-desktop-post-strip-track loopino-desktop-campaign-track loopino-calendar-skeleton-track"
            padding="20px"
          >
            {skeletonCampaigns.map((_, index) => (
              <div key={`campaign-skeleton-${index}`} className="loopino-desktop-campaign-card">
                <div className="loopino-desktop-campaign-card-header">
                  <Skeleton variant="rounded" width={100} height={20} shimmer />
                </div>
                <div className="loopino-desktop-campaign-card-body">
                  <Skeleton width="70%" height={18} shimmer />
                  <Skeleton width="50%" height={12} shimmer />
                </div>
              </div>
            ))}
          </DesktopHorizontalScroll>
        </div>

        <div className="loopino-calendar-panel">
          <div className="loopino-calendar-panel-header">
            <div className="loopino-calendar-header-left">
              <div className="loopino-calendar-eyebrow">
                <Skeleton width={120} height={12} shimmer />
              </div>
              <h3 className="loopino-calendar-title">
                <Skeleton width={320} height={28} shimmer />
              </h3>
              <p className="loopino-calendar-subtitle">
                <Skeleton width={300} height={14} shimmer />
              </p>
            </div>
            <div className="loopino-calendar-actions">
              <div className="loopino-calendar-navigation">
                <Skeleton variant="circle" width={32} height={32} shimmer />
                <Skeleton variant="circle" width={32} height={32} shimmer />
                <Skeleton variant="circle" width={32} height={32} shimmer />
              </div>
              <Skeleton variant="rounded" width={130} height={36} shimmer />
              <Skeleton variant="rounded" width={120} height={40} shimmer />
            </div>
          </div>

          <div className="loopino-calendar-grid loopino-calendar-grid-skeleton" style={calendarGridStyle}>
            <div className="loopino-calendar-week-header">
              <div className="loopino-calendar-time-column-header">
                <Skeleton width="60%" height={12} shimmer />
              </div>
              {skeletonDays.map((_, index) => (
                <div key={`day-header-skeleton-${index}`} className="loopino-calendar-day-header">
                  <Skeleton width="40%" height={12} shimmer />
                  <Skeleton width="70%" height={18} shimmer />
                </div>
              ))}
            </div>

            <div className="loopino-calendar-body loopino-calendar-body-skeleton">
              <div className="loopino-calendar-week-grid-body">
                {skeletonHours.map((_, hourIndex) => (
                  <div key={`hour-row-skeleton-${hourIndex}`} className="loopino-calendar-week-row">
                    <div className="loopino-calendar-hour-marker">
                      <Skeleton width="50%" height={12} shimmer />
                    </div>
                    {skeletonDays.map((_, dayIndex) => {
                      const hasEvent = (hourIndex + dayIndex) % 3 === 0;
                      const hasSecondLine = (hourIndex + dayIndex) % 5 === 0;

                      return (
                        <div key={`hour-cell-skeleton-${hourIndex}-${dayIndex}`} className="loopino-calendar-hour-cell">
                          <div className="loopino-calendar-hour-events">
                            {hasEvent ? (
                              <div className="loopino-calendar-skeleton-event">
                                <Skeleton variant="rounded" width="100%" height={64 + ((dayIndex + hourIndex) % 2) * 12} shimmer />
                                {hasSecondLine && <Skeleton width="60%" height={12} shimmer />}
                              </div>
                            ) : (
                              <div className="loopino-calendar-hour-empty" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const getEventVisuals = (post) => {
    const originalThumbnail = getFirstMediaUrl(post);
    const isVideo = isVideoFile(originalThumbnail);
    const thumbnailKey = `${post.id}-${originalThumbnail}`;
    const thumbnail = isVideo
      ? videoThumbnails[thumbnailKey] || originalThumbnail
      : originalThumbnail;
    const loadingThumb = isVideo && thumbnailsLoading[thumbnailKey];
    const platform = post.platform || post.social_platform || 'instagram';
    const statusInfo = getCalendarPostStatusInfo(post);
    const postDate = new Date(post.scheduledDate || post.scheduled_time || Date.now());
    const isInPast = postDate < new Date();
    const isPosted = post.posted === true;
    const badgeVariant = (() => {
      if (isPosted) return 'posted';
      if (isInPast && post.posted === false) {
        if (['pending', 'scheduled', 'publishing'].includes(statusInfo.badgeVariant)) {
          return 'failed';
        }
      }
      return statusInfo.badgeVariant;
    })();

    return {
      thumbnail,
      loadingThumb,
      platform,
      badgeVariant,
      isVideo
    };
  };

  const renderWeekGrid = () => {
    const minuteHeightPx = HOUR_CELL_MIN_HEIGHT / 60;
    const minEventHeightPx = Math.max(MIN_EVENT_DURATION_MINUTES * minuteHeightPx, 180);

    const renderHourCell = (day, hour, dayIndex) => {
      const dayKey = day.toDateString();
      const entries = eventsByDayAndHour.get(dayKey)?.get(hour) || [];
      const isTodayCell = isToday(day);
      const isLastDay = dayIndex === weekDays.length - 1;
      const isCurrentHour = isTodayCell && currentTime.getHours() === hour;
      const nowTop = (currentTime.getMinutes() / 60) * 100;
      let occupiedHeight = 0;

      return (
        <div
          key={`${day.toISOString()}-${hour}`}
          className={`loopino-calendar-hour-cell ${isTodayCell ? 'is-today' : ''} ${isLastDay ? 'is-last-day' : ''}`}
        >
          {isCurrentHour && (
            <div className="loopino-calendar-now-line" style={{ top: `${nowTop}%` }}>
              <span />
            </div>
          )}
          <div className="loopino-calendar-hour-events">
            {entries.length === 0 ? (
              <div className="loopino-calendar-hour-empty" />
            ) : (
              entries.map((entry, idx) => {
                const { post, scheduledAt, durationMinutes, minutes } = entry;
                const { thumbnail, loadingThumb, platform, badgeVariant, isVideo } = getEventVisuals(post);
                const postId = getPostIdentifier(post);
                const profileName = getPostProfileName(post);
                const postTitle = post.title || profileName || '';
                const hasThumbnail = Boolean(thumbnail);
                const statusBorderColor = getStatusBorderColor(badgeVariant);
                const isTarget = highlightedPostId && postId && highlightedPostId === postId;
                const postKey = postId || `${day.toISOString()}-${hour}-${idx}`;

                const startOffset = Math.max(minutes * minuteHeightPx, occupiedHeight);
                const eventHeight = Math.max(minEventHeightPx, durationMinutes * minuteHeightPx);
                const marginTop = Math.max(0, startOffset - occupiedHeight);
                occupiedHeight = startOffset + eventHeight + STACK_GAP_PX;

                return (
                  <button
                    key={postKey}
                    type="button"
                    className={`loopino-calendar-event ${badgeVariant} ${isTarget ? 'is-target' : ''}`}
                    style={{ borderColor: statusBorderColor }}
                    ref={isTarget ? targetEventRef : null}
                    onClick={() => handlePostCardClick(post)}
                  >
                    <div className="loopino-calendar-event-body">
                      <div className={`loopino-calendar-event-thumb ${hasThumbnail ? '' : 'is-empty'}`}>
                        {hasThumbnail ? (
                          loadingThumb ? (
                            <div className="loopino-calendar-thumb-loading">
                              <div className="loopino-calendar-thumb-spinner" />
                            </div>
                          ) : (
                            <img src={thumbnail} alt="" />
                          )
                        ) : (
                          <div className="loopino-calendar-thumb-placeholder" />
                        )}

                        <div className="loopino-calendar-thumb-overlay">
                          <div className="loopino-calendar-thumb-meta">
                            <div className="loopino-calendar-thumb-meta-left">
                              <span className="loopino-calendar-event-time">
                                {formatTimeLabel(scheduledAt)}
                              </span>
                            </div>
                            <div className="loopino-calendar-thumb-meta-right">
                              {isVideo && !loadingThumb && (
                                <span className="loopino-calendar-thumb-pill" aria-hidden="true">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <polygon points="5,3 19,12 5,21" fill="currentColor" />
                                  </svg>
                                </span>
                              )}
                              <span className="loopino-calendar-event-platform">
                                <PlatformIcon platform={platform} size={12} />
                              </span>
                            </div>
                          </div>
                          {postTitle && <p className="loopino-calendar-thumb-title">{postTitle}</p>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="loopino-calendar-grid">
        <div className="loopino-calendar-week-header">
          <div className="loopino-calendar-time-column-header">{t('calendar.labels.time', { defaultValue: 'Orario' })}</div>
          {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className={`loopino-calendar-day-header ${isToday(day) ? 'is-today' : ''}`}
            >
              <span className="loopino-calendar-day-name">{weekdayFormatter.format(day)}</span>
              <div className="loopino-calendar-day-number-wrap">
                <span className="loopino-calendar-day-number">{day.getDate()}</span>
                <span className="loopino-calendar-day-month">{monthLabelFormatter.format(day)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="loopino-calendar-body" ref={scrollRef}>
          <div className="loopino-calendar-week-grid-body">
            {hours.map((hour) => (
              <div
                key={`week-row-${hour}`}
                className="loopino-calendar-week-row"
                ref={(node) => {
                  if (!node) {
                    hourRowRefs.current.delete(hour);
                  } else {
                    hourRowRefs.current.set(hour, node);
                  }
                }}
              >
                <div className="loopino-calendar-hour-marker">
                  <span>{formatHourLabel(hour)}</span>
                </div>
                {weekDays.map((day, index) => renderHourCell(day, hour, index))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderAgenda = () => (
    <div className="loopino-calendar-agenda">
      {weekDays.map((day) => {
        const events = postsByDay.get(day.toDateString()) || [];
        const dayLabel = `${weekdayFormatter.format(day)} ${dayMonthYearFormatter.format(day)}`;
        return (
          <div key={day.toISOString()} className="loopino-calendar-agenda-day-block">
            <div className="loopino-calendar-agenda-day-header">
              <div>
                <p className="loopino-calendar-agenda-eyebrow">{weekdayFormatter.format(day)}</p>
                <h4>{dayMonthYearFormatter.format(day)}</h4>
              </div>
              <CampaignBadge label={`${events.length} ${t('calendar.labels.posts', { defaultValue: 'post' })}`} active={events.length > 0} />
            </div>

            {events.length > 0 ? (
              <div className="loopino-calendar-agenda-rail">
                <DesktopHorizontalScroll trackClassName="loopino-calendar-agenda-track" padding="8px">
                  {events.map((post) => {
                    const scheduledAt = new Date(post.scheduledDate || post.scheduled_time || Date.now());
                    const { thumbnail, loadingThumb, platform, badgeVariant, isVideo } = getEventVisuals(post);
                    const profileName = getPostProfileName(post);
                    const postTitle = post.title || profileName || t('calendar.labels.profile', { defaultValue: 'Profilo' });
                    const postId = getPostIdentifier(post);
                    const isTarget = highlightedPostId && postId && highlightedPostId === postId;
                    const postKey = postId || scheduledAt.toISOString();

                    return (
                      <button
                        key={postKey}
                        type="button"
                        className={`loopino-calendar-agenda-card ${badgeVariant} ${isTarget ? 'is-target' : ''}`}
                        ref={isTarget ? targetEventRef : null}
                        onClick={() => handlePostCardClick(post)}
                      >
                        <div className="loopino-calendar-agenda-card-meta">
                          <div className="loopino-calendar-agenda-meta-left">
                            <StatusDot variant={badgeVariant} />
                            <span className="loopino-calendar-agenda-time">{formatTimeLabel(scheduledAt)}</span>
                          </div>
                          <span className="loopino-calendar-agenda-day">{dayLabel}</span>
                        </div>

                        <div className="loopino-calendar-agenda-card-body">
                          {thumbnail && (
                            <div className="loopino-calendar-event-thumb">
                              {loadingThumb ? (
                                <div className="loopino-calendar-thumb-loading">
                                  <div className="loopino-calendar-thumb-spinner" />
                                </div>
                              ) : (
                                <img src={thumbnail} alt="" />
                              )}
                              {isVideo && !loadingThumb && (
                                <div className="loopino-calendar-thumb-video">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <polygon points="5,3 19,12 5,21" fill="white" stroke="white" strokeWidth="2" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          )}

                          <p className="loopino-calendar-event-title">{postTitle}</p>
                        </div>

                        <div className="loopino-calendar-agenda-footer">
                          <div className="loopino-calendar-agenda-meta-left">
                            <PlatformIcon platform={platform} size={14} />
                          </div>
                          {post.campaignName && (
                            <span className="loopino-calendar-agenda-campaign">{post.campaignName}</span>
                          )}
                        </div>
                        </button>
                      );
                    })}
                </DesktopHorizontalScroll>
              </div>
            ) : (
              <div className="loopino-calendar-empty">
                <p>{t('calendar.states.emptyDay', { defaultValue: 'Nessun post per questo giorno' })}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderDatePicker = () => {
    if (!isDatePickerOpen) return null;
    return (
      <div className="loopino-calendar-overlay" onClick={() => setIsDatePickerOpen(false)}>
        <div className="loopino-calendar-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="loopino-calendar-dialog-header">
            <button
              type="button"
              className="loopino-calendar-icon-button"
              onClick={() => {
                const prev = new Date(pickerMonth);
                prev.setMonth(prev.getMonth() - 1);
                setPickerMonth(prev);
              }}
            >
              ‹
            </button>
            <div>
              <p className="loopino-calendar-dialog-eyebrow">{t('calendar.labels.month', { defaultValue: 'Mese' })}</p>
              <h4>{monthYearFormatter.format(pickerMonth)}</h4>
            </div>
            <button
              type="button"
              className="loopino-calendar-icon-button"
              onClick={() => {
                const next = new Date(pickerMonth);
                next.setMonth(next.getMonth() + 1);
                setPickerMonth(next);
              }}
            >
              ›
            </button>
          </div>
          <div className="loopino-calendar-dialog-weekdays">
            {datePickerWeekdays.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="loopino-calendar-dialog-grid">
            {getDaysInMonth(pickerMonth).map((date, index) => {
              const isCurrent = date && isToday(date);
              const sameWeek = date && (() => {
                const weekStart = weekDays[0];
                const weekEnd = weekDays[6];
                return date >= weekStart && date <= weekEnd;
              })();

              return (
                <button
                  key={`${index}-${date?.toISOString?.() || 'empty'}`}
                  type="button"
                  className={`loopino-calendar-dialog-day ${!date ? 'is-empty' : ''} ${isCurrent ? 'is-today' : ''} ${sameWeek ? 'is-selected' : ''}`}
                  disabled={!date}
                  onClick={() => date && handleDateSelect(date)}
                >
                  {date ? date.getDate() : ''}
                </button>
              );
            })}
          </div>
          <div className="loopino-calendar-dialog-actions">
            <button type="button" onClick={navigateToToday} className="loopino-calendar-ghost-button">
              {t('calendar.actions.goToday', { defaultValue: 'Oggi' })}
            </button>
            <button type="button" onClick={() => setIsDatePickerOpen(false)} className="loopino-calendar-solid-button">
              {t('calendar.actions.close', { defaultValue: 'Chiudi' })}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (calendarLoading) {
    return (
      <div className="loopino-desktop-shell loopino-desktop-calendar-shell">
        {renderLoadingSkeleton()}
      </div>
    );
  }

  if (calendarError) {
    return (
      <div className="loopino-desktop-shell loopino-desktop-calendar-shell">
        <div className="loopino-calendar-panel">
          <div className="loopino-calendar-error">
            <p>{t('calendar.errors.title', { defaultValue: 'Errore nel caricamento del calendario' })}</p>
            <span>{calendarError}</span>
            <button type="button" className="loopino-calendar-solid-button" onClick={loadCalendarPosts}>
              {t('calendar.retry', { defaultValue: 'Riprova' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="loopino-desktop-shell loopino-desktop-calendar-shell">
      <header className="loopino-desktop-post-review-header loopino-desktop-calendar-header">
        <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <h1>{t('calendar.desktop.title', { defaultValue: 'Calendario editoriale' })}</h1>
          <p className="loopino-desktop-post-review-summary">
            {t('calendar.desktop.subtitle', {
              defaultValue: 'Visualizza e controlla i post pianificati. Filtra per campagna, naviga per settimana o passa alla vista agenda ispirata al nuovo componente calendario.'
            })}
          </p>
        </div>
        <div className="loopino-calendar-header-actions">
          <div className="loopino-calendar-chip-group">
            {selectedCampaignLabels.slice(0, 2).map((label) => (
              <CampaignBadge key={label} label={label} active />
            ))}
            {selectedCampaignLabels.length > 2 && (
              <CampaignBadge label={`+${selectedCampaignLabels.length - 2}`} active />
            )}
      </div>
        <div className="loopino-calendar-view-toggle">
          <button
            type="button"
            className={`loopino-calendar-ghost-button ${viewMode === 'week' ? 'is-active' : ''}`}
              onClick={() => setViewMode('week')}
            >
              {t('calendar.views.week', { defaultValue: 'Settimana' })}
            </button>
            <button
              type="button"
              className={`loopino-calendar-ghost-button ${viewMode === 'agenda' ? 'is-active' : ''}`}
              onClick={() => setViewMode('agenda')}
            >
              {t('calendar.views.agenda', { defaultValue: 'Agenda' })}
            </button>
          </div>
        </div>
      </header>

      <DesktopCampaignSelector
        campaigns={allCampaigns}
        selectionMode="multiple"
        selectedCampaignIds={selectedCampaignIds}
        onCampaignSelectionChange={handleCampaignSelectionChange}
        requireConfirmation={false}
        title={t('calendar.filters.select', { defaultValue: 'Seleziona campagne' })}
        eyebrow={t('calendar.filters.campaigns', { defaultValue: 'Campagne' })}
        description={t('calendar.desktop.campaignSelectorHint', {
          defaultValue: 'Filtra il calendario scegliendo le campagne direttamente da questa strip.'
        })}
      />

      <div className="loopino-calendar-panel">
        <div className="loopino-calendar-panel-header">
          <div className="loopino-calendar-header-left">
            <div className="loopino-calendar-eyebrow">{t('calendar.labels.week', { defaultValue: 'Settimana' })}</div>
            <h3 className="loopino-calendar-title">{formattedWeekRange}</h3>
          </div>
          <div className="loopino-calendar-actions">
            <div className="loopino-calendar-navigation">
              <button
                type="button"
                className="loopino-calendar-icon-button"
                onClick={() => handleWeekChange(-1)}
                aria-label={t('calendar.actions.prevWeek', { defaultValue: 'Settimana precedente' })}
              >
                ‹
              </button>
              <button
                type="button"
                className="loopino-calendar-icon-button"
                onClick={navigateToToday}
                aria-label={t('calendar.actions.goToday', { defaultValue: 'Vai a oggi' })}
              >
                •
              </button>
              <button
                type="button"
                className="loopino-calendar-icon-button"
                onClick={() => handleWeekChange(1)}
                aria-label={t('calendar.actions.nextWeek', { defaultValue: 'Settimana successiva' })}
              >
                ›
              </button>
            </div>
            <button type="button" className="loopino-calendar-ghost-button" onClick={openDatePicker}>
              {t('calendar.actions.chooseDate', { defaultValue: 'Scegli data' })}
            </button>
            <button type="button" className="loopino-calendar-solid-button" onClick={loadCalendarPosts}>
              {t('common.actions.refresh', { defaultValue: 'Aggiorna' })}
            </button>
          </div>
        </div>

        {viewMode === 'week' ? renderWeekGrid() : renderAgenda()}
      </div>

      {renderDatePicker()}
    </div>
  );
};

export default DesktopCalendarPage;
